'use client';

import 'BomdisoftComponents/rating/styles.css';
import { joinClasses } from 'BomdisoftComponents/html/index';
import { RATING_SIZE_PX, RATING_STEPS, ratingLabel, STAR_PATH, toStarFill } from 'BomdisoftComponents/rating/render';
import { getLanguageFromLocale, ratingSelectorT, ratingValueT } from '@bomdisoft/translations';
import { useParams } from 'next/navigation';
import { useId, useState } from 'react';

interface CustomRatingProps {
  rating?: number;
  size?: 'medium' | 'large' | 'small';
  readOnly?: boolean;
  showLabels?: boolean;
  onConfirm?: (value: number) => void;
  className?: string;
}

const Star = ({ fill }: { fill?: number }) => (
  <span className='bomdisoft-rating-star'>
    <svg aria-hidden='true' fill='currentColor' viewBox='0 0 24 24'>
      <path d={STAR_PATH} />
    </svg>
    <span className='bomdisoft-rating-star-fill' style={fill === undefined ? undefined : ({ '--star-fill': fill } as React.CSSProperties)}>
      <svg aria-hidden='true' fill='currentColor' viewBox='0 0 24 24'>
        <path d={STAR_PATH} />
      </svg>
    </span>
  </span>
);

export const CustomRating = ({ rating = 0, size = 'medium', readOnly = true, showLabels = true, onConfirm = () => null, className }: CustomRatingProps) => {
  const params = useParams();
  const currentLanguage = getLanguageFromLocale(params?.lang as string) ?? 'english';
  const groupName = useId();

  const [value, setValue] = useState<number>(rating);

  const handleChange = (newValue: number) => {
    if (readOnly) return;
    setValue(newValue);
    onConfirm(newValue);
  };

  const holderClasses = joinClasses('bomdisoft-rating', className);
  const sizeStyle = { '--rating-size': `${RATING_SIZE_PX[size]}px` } as React.CSSProperties;

  if (readOnly) {
    return (
      <span aria-label={ratingValueT[currentLanguage].replace('{value}', String(value))} className={holderClasses} data-size={size} role='img' style={sizeStyle}>
        {[1, 2, 3, 4, 5].map(star => (
          <Star fill={toStarFill(star, value)} key={star} />
        ))}
        {showLabels && <span className='bomdisoft-rating-label'>{ratingLabel(value, currentLanguage)}</span>}
      </span>
    );
  }

  return (
    <span className={holderClasses} data-size={size} style={sizeStyle}>
      <fieldset aria-label={ratingSelectorT[currentLanguage]} className='bomdisoft-rating-group'>
        {[1, 2, 3, 4, 5].map(star => (
          <Star key={star} />
        ))}
        {RATING_STEPS.map(halfValue => (
          <label className='bomdisoft-rating-half' data-side={Number.isInteger(halfValue) ? 'right' : 'left'} data-star={Math.ceil(halfValue)} data-value={halfValue} key={halfValue}>
            <input aria-label={`${halfValue} stars`} checked={value === halfValue} name={groupName} onChange={() => handleChange(halfValue)} type='radio' value={halfValue} />
          </label>
        ))}
      </fieldset>
      {showLabels && (
        <span aria-live='polite' className='bomdisoft-rating-label'>
          {RATING_STEPS.map(step => (
            <span data-value={step} key={step}>
              {ratingLabel(step, currentLanguage)}
            </span>
          ))}
        </span>
      )}
    </span>
  );
};
